import React from "react";
import { Color } from "config/colors";
import { useTranslation } from "react-i18next";
import { Logo } from "components/nav/logo/logo.styled";
import { Text } from "components/Typography/text.styled";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";
import SimflexityPayLogo from "images/logo/svg/simflexityPay-logo.svg";
import { FontFamily } from "config/font";
import { ManualPaidContent, ManualPaidContainer } from "../tips.styled";
import UndoIcon from "@material-ui/icons/Undo";
import { TipDisplayStatus } from "model/Tip";

interface TipDisplayStatusProps {
  status: TipDisplayStatus;
  onUndo?: () => void;
  undoLoading?: boolean;
}

export const TipDisplayStatusIndicator: React.FC<TipDisplayStatusProps> = (
  props,
) => {
  const { t } = useTranslation();

  switch (props.status) {
    case TipDisplayStatus.Created:
      return (
        <Text fontFamily={FontFamily.MontserratSemiBold}>
          {t("CompanyTip.Created")}
        </Text>
      );

    case TipDisplayStatus.ReadyToPayout:
      return (
        <Text fontFamily={FontFamily.MontserratSemiBold}>
          {t("CompanyTip.ReadyToPayout")}
        </Text>
      );

    case TipDisplayStatus.Saved:
      return (
        <Text fontFamily={FontFamily.MontserratSemiBold}>
          {t("CompanyTip.SavedTip")}
        </Text>
      );

    case TipDisplayStatus.PayoutInProgress:
      return (
        <Text
          fontFamily={FontFamily.MontserratSemiBold}
          color={Color.MidnightBlue}
        >
          {t("CompanyTip.PayoutInProgress")}
        </Text>
      );

    case TipDisplayStatus.Paid:
      return (
        <Logo
          src={SimflexityPayLogo}
          style={{
            height: "18px",
            width: "auto",
            marginLeft: "10px",
          }}
          alt="SimflexityPay logo"
        />
      );

    case TipDisplayStatus.PartlyPaid:
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <WarningIcon
            fontSize="small"
            style={{ color: Color.LighterYellow }}
          />
          <Text
            color={Color.LighterYellow}
            fontFamily={FontFamily.MontserratSemiBold}
            style={{ margin: 0, fontSize: "14px" }}
          >
            {t("CompanyTip.PartlyPaid")}
          </Text>
        </div>
      );

    case TipDisplayStatus.Failed:
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <ErrorIcon fontSize="small" style={{ color: Color.Destructive }} />
          <Text
            color={Color.Destructive}
            fontFamily={FontFamily.MontserratSemiBold}
            style={{ margin: 0, fontSize: "14px" }}
          >
            {t("CompanyTip.Failed")}
          </Text>
        </div>
      );

    case TipDisplayStatus.PaidManually:
      return (
        <ManualPaidContainer>
          <ManualPaidContent>
            <CheckCircleIcon
              fontSize="small"
              style={{ color: Color.ModernGreen }}
            />

            <Text
              color={Color.ModernGreen}
              fontFamily={FontFamily.MontserratSemiBold}
              style={{
                margin: 0,
                whiteSpace: "nowrap",
                lineHeight: 1.1,
              }}
              title={t("CompanyTip.PaidManually")}
            >
              {t("CompanyTip.ManuallyPaid")}
            </Text>

            {props.onUndo && (
              <UndoIcon
                fontSize="small"
                onClick={props.onUndo}
                style={{
                  cursor: "pointer",
                  marginLeft: "6px",
                  color: Color.MidnightBlue,
                }}
              />
            )}
          </ManualPaidContent>
        </ManualPaidContainer>
      );

    default:
      return <Text />;
  }
};
